<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { driver } from 'driver.js'
  import 'driver.js/dist/driver.css'
  import '@/assets/driverjs.css'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps<{
    forceShow?: boolean
  }>()

  const ONBOARDING_COMPLETED_KEY = 'document-templates-onboarding-completed'

  const { t } = useTranslation({
    en: {
      step1Title: 'New: Create & edit document templates',
      step1Description: 'Upload a document to get started.',
      step2Title: 'Want to use the document template builder with an existing document?',
      step2Description: 'No problem, just upload the existing document again.',
      nextButton: 'Next',
      prevButton: 'Previous',
      doneButton: 'Done'
    },
    de: {
      step1Title: 'Neu: Dokumentvorlagen erstellen & bearbeiten',
      step1Description: 'Dokument hochladen, um zu starten.',
      step2Title:
        'Sie möchten den Dokumentvorlagen-Builder mit einem bestehenden Dokument verwenden?',
      step2Description: 'Kein Problem, laden Sie das Dokument einfach erneut hoch.',
      nextButton: 'Weiter',
      prevButton: 'Zurück',
      doneButton: 'Fertig'
    }
  })

  const markOnboardingAsCompleted = () => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
  }

  const hasCompletedOnboarding = () => {
    return !props.forceShow && localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true'
  }

  const startTour = () => {
    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: [
        {
          element: '[data-cy="DocumentTemplatesCreateButton"]',
          popover: {
            title: t('step1Title'),
            description: t('step1Description'),
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-cy="DocumentTemplatesCreateButton"]',
          popover: {
            title: t('step2Title'),
            description: t('step2Description'),
            side: 'bottom',
            align: 'start'
          }
        }
      ],
      onDestroyed: () => {
        markOnboardingAsCompleted()
      },
      onHighlightStarted: () => {
        const button = document.querySelector('[data-cy="DocumentTemplatesCreateButton"]')
        if (button) {
          button.addEventListener(
            'click',
            () => {
              driverObj.destroy()
            },
            { once: true }
          )
        }
      },
      nextBtnText: t('nextButton'),
      prevBtnText: t('prevButton'),
      doneBtnText: t('doneButton')
    })

    driverObj.drive()
  }

  onMounted(() => {
    if (hasCompletedOnboarding()) {
      return
    }

    // Wait for the button to be rendered
    setTimeout(() => {
      const button = document.querySelector('[data-cy="DocumentTemplatesCreateButton"]')
      if (button) {
        startTour()
      }
    }, 500)
  })

  defineExpose({
    startTour
  })
</script>

<template>
  <div></div>
</template>
